<template>
  <v-dialog
    :value="visible"
    @input="$emit('close')"
    max-width="800px"
    persistent
    scrollable
  >
    <v-card class="mb-0">
      <v-card-title class="headline primary white--text">
        <v-icon left color="white">mdi-trophy</v-icon>
        {{ title }}
        <v-spacer></v-spacer>
        <v-btn icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-4" style="max-height: 600px">
        <!-- INFORMACIÓN DE LA COMPETENCIA -->
        <v-alert type="info" text dense class="mb-4">
          <div class="d-flex align-center">
            <div>
              <strong>{{ nombreCompetencia }}</strong
              ><br />
              <span class="text-caption">
                {{ categoria }} | {{ genero }} |
                {{ new Date().toLocaleDateString() }}
              </span>
            </div>
          </div>
        </v-alert>

        <!-- INSTRUCCIONES -->
        <v-alert color="amber lighten-4" border="left" dense class="mb-4">
          <div class="text-body-2">
            📋 Ingrese manualmente los puntos para cada jugador según su mérito.
            <br />
            ✅ Los jugadores están ordenados por posición (mejor a peor).
            <br />
            👥 En dobles, ambos jugadores aparecen uno debajo del otro.
          </div>
        </v-alert>

        <!-- TABLA DE JUGADORES Y PUNTOS -->
        <v-simple-table class="elevation-2">
          <template v-slot:default>
            <thead>
              <tr>
                <th v-if="!props.idRanking" class="text-left" width="80">
                  Orden
                </th>
                <th class="text-left">Jugador</th>
                <th v-if="!props.idRanking" class="text-left" width="200">
                  Mérito
                </th>
                <th class="text-center" width="150">Puntos</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(jugador, index) in jugadores"
                :key="`${jugador.idJugador}-${index}`"
                :class="{
                  'primera-posicion': jugador.ordenMerito === 1,
                  'pareja-dobles': esSegundoDePareja(index),
                }"
              >
                <td class="text-left" v-if="!props.idRanking">
                  <v-chip
                    small
                    :color="getColorOrden(jugador.ordenMerito)"
                    :dark="jugador.ordenMerito <= 3"
                  >
                    {{ jugador.ordenMerito }}º
                  </v-chip>
                </td>
                <td class="text-left">
                  <div class="d-flex align-center">
                    <v-icon small color="grey" class="mr-2">
                      mdi-account-multiple
                    </v-icon>
                    <div>
                      <div class="font-weight-medium">
                        {{ jugador.nombreJugador }}
                      </div>
                      <div class="text-caption grey--text">
                        ID: {{ jugador.idJugador }}
                      </div>
                    </div>
                  </div>
                </td>
                <td v-if="!props.idRanking" class="text-left">
                  <v-chip small outlined>
                    {{ jugador.descripcionMerito }}
                  </v-chip>
                </td>
                <td class="text-center">
                  <v-text-field
                    v-model.number="jugador.puntosAsignados"
                    type="number"
                    dense
                    outlined
                    hide-details
                    min="0"
                    style="max-width: 100px; margin: 0 auto"
                    placeholder="0"
                    @keypress="onlyNumber"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small color="amber darken-2">mdi-star</v-icon>
                    </template>
                  </v-text-field>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- RESUMEN -->
        <v-row class="mt-4">
          <v-col cols="12" sm="4">
            <v-card color="primary" dark>
              <v-card-text class="text-center">
                <div class="text-h5">{{ jugadores.length }}</div>
                <div class="text-caption">Jugadores</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4">
            <v-card color="success" dark>
              <v-card-text class="text-center">
                <div class="text-h5">{{ puntosMaximos }}</div>
                <div class="text-caption">Puntos Máximos</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4">
            <v-card color="amber darken-2" dark>
              <v-card-text class="text-center">
                <div class="text-h5">{{ puntosTotal }}</div>
                <div class="text-caption">Puntos Totales</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-btn text @click="$emit('close')"> Cancelar </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          large
          @click="finalizar"
          :disabled="!jugadores.length"
        >
          <v-icon left>mdi-check</v-icon>
          {{ title }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { JugadorParaFinalizar } from "@/utils/rankings";

const props = defineProps({
  visible: Boolean,
  jugadores: {
    type: Array as () => JugadorParaFinalizar[],
    default: () => [],
  },
  tipoCompetencia: {
    type: String as () => "Torneo" | "Liga",
    default: "Torneo",
  },
  nombreCompetencia: String,
  categoria: String,
  genero: String,
  idRanking: {
    type: [Number, undefined],
  },
});

const emit = defineEmits(["close", "finalizar"]);

const puntosMaximos = computed(() => {
  if (!props.jugadores.length) return 0;
  return Math.max(...props.jugadores.map((j) => j.puntosAsignados || 0));
});

const puntosTotal = computed(() => {
  return props.jugadores.reduce((sum, j) => sum + (j.puntosAsignados || 0), 0);
});

const title = computed(() => {
  return !props.idRanking
    ? `Finalizar ${props.tipoCompetencia}`
    : "Actualizar puntos";
});

function getColorOrden(orden: number): string {
  if (orden === 1) return "amber darken-1";
  if (orden === 2) return "grey lighten-1";
  if (orden === 3) return "orange darken-3";
  return "grey";
}

function esSegundoDePareja(index: number): boolean {
  if (index === 0) return false;

  const jugadorActual = props.jugadores[index];
  const jugadorAnterior = props.jugadores[index - 1];

  return jugadorActual.ordenMerito === jugadorAnterior.ordenMerito;
}

function finalizar() {
  emit("finalizar", props.jugadores);
}

function onlyNumber($event: any) {
  let keyCode = $event.keyCode ? $event.keyCode : $event.which;
  if (keyCode < 48 || keyCode > 57) {
    $event.preventDefault();
  }
}
</script>

<style scoped lang="scss">
.primera-posicion {
  background-color: #fff9c4 !important;

  td {
    font-weight: 600;
  }
}

.pareja-dobles {
  border-left: 3px solid #2196f3;
  background-color: #e3f2fd !important;
}

.v-simple-table {
  thead {
    background-color: #f5f5f5;

    th {
      font-weight: 700 !important;
    }
  }

  tbody {
    tr {
      transition: background-color 0.2s;

      &:hover {
        background-color: #f9f9f9 !important;
      }
    }
  }
}

.text-caption {
  font-size: 0.75rem;
  line-height: 1.2;
}
</style>
